import type { ExpertSummary } from "../../api";

/** CSV table preview. Accepts pre-parsed `headers` + `rows`; if absent,
 *  shows a sample table. The expert's color tints the header row + the
 *  bottom rule. Alt-row "ledger tape" tint via --tape.
 */
export function CsvPreview({
  expert,
  headers,
  rows,
}: {
  expert: ExpertSummary;
  headers?: string[];
  rows?: string[][];
}) {
  const c = expert.color_token;
  const data = headers && rows
    ? { headers, rows }
    : sampleData();
  return (
    <div style={{ maxWidth: 880, margin: "0 auto" }}>
      <div className="card" style={{ overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {data.headers.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "10px 14px",
                    fontSize: 11,
                    fontFamily: "var(--mono)",
                    letterSpacing: ".06em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    borderBottom: `1px solid var(--${c}-deep)`,
                    background: `var(--${c}-tint)`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr
                key={i}
                style={{
                  background: i % 2 ? "var(--tape)" : "var(--card)",
                }}
              >
                {r.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      padding: "10px 14px",
                      fontSize: 13,
                      fontFamily: j === 0 ? "var(--sans)" : "var(--mono)",
                      fontWeight: i === data.rows.length - 1 ? 600 : 400,
                      borderBottom:
                        i < data.rows.length - 1
                          ? "1px solid var(--rule)"
                          : "none",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="dim" style={{ marginTop: 10, fontSize: 12 }}>
        {headers && rows
          ? "Sortable, filterable in a future revision."
          : "Sample preview — real artifact content arrives via api endpoint."}
      </div>
    </div>
  );
}

function sampleData(): { headers: string[]; rows: string[][] } {
  return {
    headers: ["Field", "Value", "Source"],
    rows: [
      ["sample row 1", "—", "transcript@00:01:15"],
      ["sample row 2", "—", "transcript@00:02:30"],
      ["TOTAL", "—", "—"],
    ],
  };
}
